/**
 * RyElement - Base class for all ry-ui components
 *
 * Provides:
 * - Lifecycle hooks with auto-cleanup
 * - Event helper methods
 * - State management via data attributes
 * - DOM query helpers
 */

import type { CleanupFn, StateChangeDetail } from '../types.js';

export class RyElement extends HTMLElement {
  // Store for cleanup functions
  #cleanupFns: CleanupFn[] = [];

  // Store for MutationObserver
  #observer: MutationObserver | null = null;

  constructor() {
    super();
  }

  /**
   * Called when element is added to DOM.
   * Override setup() in subclass instead of this.
   */
  connectedCallback(): void {
    // Mark as initialized
    this.setAttribute('data-ry-initialized', '');

    // Call subclass setup
    this.setup?.();
  }

  /**
   * Called when element is removed from DOM.
   * Automatically cleans up event listeners and observers.
   */
  disconnectedCallback(): void {
    // Run all cleanup functions
    this.#cleanupFns.forEach(fn => fn());
    this.#cleanupFns = [];

    // Disconnect observer
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    // Call subclass teardown
    this.teardown?.();
  }

  /**
   * Override in subclass for component initialization
   */
  setup?(): void;

  /**
   * Override in subclass for cleanup
   */
  teardown?(): void;

  /**
   * Add event listener with automatic cleanup on disconnect.
   */
  on<K extends keyof HTMLElementEventMap>(
    target: EventTarget,
    event: K,
    handler: (e: HTMLElementEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): (e: HTMLElementEventMap[K]) => void;

  on(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): EventListener;

  on(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options: AddEventListenerOptions = {}
  ): EventListener {
    const boundHandler = handler.bind(this) as EventListener;
    target.addEventListener(event, boundHandler, options);

    this.#cleanupFns.push(() => {
      target.removeEventListener(event, boundHandler, options);
    });

    return boundHandler;
  }

  /**
   * Emit a custom event with ry: prefix.
   */
  emit<T = void>(name: string, detail?: T, options: CustomEventInit = {}): boolean {
    const event = new CustomEvent(`ry:${name}`, {
      bubbles: true,
      cancelable: true,
      detail,
      ...options
    });
    return this.dispatchEvent(event);
  }

  /**
   * Get/set component state via data attribute.
   */
  get state(): string {
    return this.dataset.ryState ?? 'closed';
  }

  set state(value: string) {
    const oldState = this.state;
    if (oldState !== value) {
      this.dataset.ryState = value;
      this.emit<StateChangeDetail>('state-change', { oldState, newState: value });
    }
  }

  /**
   * Query within this element.
   */
  $<E extends Element = Element>(selector: string): E | null {
    return this.querySelector<E>(selector);
  }

  /**
   * Query all within this element.
   */
  $$<E extends Element = Element>(selector: string): E[] {
    return [...this.querySelectorAll<E>(selector)];
  }

  /**
   * Setup a MutationObserver on this element.
   */
  observe(
    callback: MutationCallback,
    options: MutationObserverInit = { childList: true, subtree: true }
  ): void {
    if (this.#observer) {
      this.#observer.disconnect();
    }

    this.#observer = new MutationObserver(callback.bind(this));
    this.#observer.observe(this, options);
  }

  /**
   * Wait for next animation frame.
   */
  nextFrame(): Promise<number> {
    return new Promise(resolve => requestAnimationFrame(resolve));
  }

  /**
   * Trap focus within a container (for modals, dropdowns).
   */
  trapFocus(container: HTMLElement): void {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusableEls = container.querySelectorAll<HTMLElement>(focusableSelectors);
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleKeydown = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl?.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl?.focus();
        }
      }
    };

    this.on(container, 'keydown', handleKeydown);

    // Focus first element
    firstEl?.focus();
  }
}
